import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AIRCRAFT_USE_OPTIONS } from '@/lib/aircraftUsage.js';
import AircraftDocuments from '../AircraftDocuments.jsx';
import { getAccountRecord } from '@/services/account.service.js';
import { usePeekStore } from '@/stores/peek-store.js';
import { openContactPeek } from '@/lib/links.js';
import PhfSubmissionSheet from '@/components/sheets/PhfSubmissionSheet.jsx';
import { getAnswerLists } from '@/services/pilots.service.js';
import MakePersonSheet from '@/components/people/MakePersonSheet.jsx';
import FactRow from './FactRow.jsx';
import TermsChain from './TermsChain.jsx';

const RosterSheet = lazy(() => import('@/components/sheets/RosterSheet.jsx'));

// THE CLIENT RECORD (CLIENT-RECORD-SPEC §5, canvas 2026-09-16). One insured:
// header facts, the terms chain, aircraft, people. Every fact is a row: label
// · value · one verb; an empty required fact is a field in place. The
// Incomplete chip IS the checklist (§5.1 + canvas): click it and the empty
// required rows are the only ones left lit. Nothing here derives a state or
// writes a fact; the record endpoint says, the doors do.

const STORAGE_OPTIONS = ['Hangared', 'Tied Down', 'Hangared and Tied Down'];
// Certificate and medical class come from the one list (GET /api/tracon/answer-lists, utils/answer-lists; David 2026-11-04):
// this page held its own medical list ("First Class" ...) that matched none of the stored values.

const AIRCRAFT_ROWS = [
  ['hull_value', 'Hull', 'money'], ['coverage', 'Coverage basis', 'text'], ['use', 'Use', 'text'],
  ['deductible_in_motion', 'Ded. in motion', 'money'], ['deductible_not_in_motion', 'Ded. not in motion', 'money'],
  ['airport', 'Base airport', 'text'], ['storage', 'Storage', 'text'], ['seats', 'Seats', 'number'],
];
const REGISTRY_ROWS = [['serial_number', 'Serial', 'text'], ['aircraft_type', 'Class', 'text'], ['engine_type', 'Engine', 'text'], ['undercarriage', 'Gear', 'text'], ['weight_class', 'Weight', 'text'], ['year', 'Year', 'number'], ['make', 'Make', 'text'], ['model', 'Model', 'text']];
const PILOT_ROWS = [
  ['certificate_type', 'Certificate', 'text'], ['ratings', 'Ratings', 'text'], ['total_flight_hours', 'Total hours', 'number'], ['hours_last_12_months', 'Last 12 months', 'number'],
  ['medical_class', 'Medical', 'text'], ['medical_date', 'Medical date', 'date'], ['basicmed_course_date', 'BasicMed course', 'date'],
  ['last_flight_review_date', 'Flight review', 'date'], ['date_of_birth', 'Date of birth', 'date'],
];
// A person's identity, edited in TRACON (ruling 1, people-create-door C5): the
// record names a writer for each (client-record.service PERSON_WRITERS, the
// person edit door) on every person card with a Contact, the named insured too.
const IDENTITY_ROWS = [['name', 'Name'], ['preferred_name', 'Preferred name'], ['email', 'Email'], ['phone', 'Phone']];
const OPTIONS = { use: AIRCRAFT_USE_OPTIONS, storage: STORAGE_OPTIONS };

function Card({ title, children, right, ...rest }) {
  return (
    <div className="rounded-card border border-border bg-surface" {...rest}>
      <div className="flex items-center gap-3 border-b border-border px-4 py-2.5">{title}<div className="ml-auto flex items-center gap-2 text-xs text-text-muted">{right}</div></div>
      <div className="px-4">{children}</div>
    </div>
  );
}

export default function ClientRecord({ accountId, accountName, onChange, refreshKey }) {
  const [rec, setRec] = useState(null);
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();
  // ?incomplete=1 (the readiness card's link) opens the record filtered.
  const [filter, setFilter] = useState(searchParams.get('incomplete') === '1');
  const [showMeta, setShowMeta] = useState(false);
  const [registryOpen, setRegistryOpen] = useState({});
  const [phfOpen, setPhfOpen] = useState(null);
  const [rosterOpen, setRosterOpen] = useState(false);
  // A contact-less pilot is not a person yet (C8, AR12): the record's make_person
  // writer opens MakePersonSheet. Null = closed.
  const [makePerson, setMakePerson] = useState(null);
  const [lists, setLists] = useState({});
  useEffect(() => { getAnswerLists().then(setLists).catch(() => setLists({})); }, []);
  const optionsFor = (f) => lists[f] || OPTIONS[f];
  const openPeek = usePeekStore((s) => s.openPeek);

  const load = useCallback(() => {
    if (!accountId) return;
    let alive = true;
    getAccountRecord(accountId)
      .then((r) => { if (alive) { setRec(r); setError(''); } })
      .catch((e) => { if (alive) setError(e?.message || 'Could not load the record'); });
    return () => { alive = false; };
  }, [accountId]);
  useEffect(() => load(), [load, refreshKey]);
  const saved = () => { load(); onChange?.(); };

  const incompleteSet = useMemo(() => new Set((rec?.incomplete || []).map((i) => `${i.subject}:${i.id}:${i.field}`)), [rec]);
  const isLit = (subject, id, field) => !filter || incompleteSet.has(`${subject}:${id}:${field}`);
  const rowProps = { accountId, accountName: rec?.account?.name || accountName, onSaved: saved, showMeta, hasOpenTerm: !!rec?.account?.current_deal_id };
  const openDoor = (writer) => {
    if (!writer) return;
    // The roster sheet is the door for storage AND for a per-term coverage
    // fact whose term has no canonical aircraft row yet (Erik, 2026-09-23:
    // the re-up session carried zero policy_aircraft rows, so every coverage
    // row said "no open term" beside an open term). Applying the roster
    // seeds the row; the coverage door can write it after that.
    if (writer.door === 'roster' || (writer.door === 'aircraft_coverage' && !writer.target?.deal_id)) setRosterOpen(true);
    // A person's identity (C5): the person sheet holds the whole edit (the
    // registry reason, the other numbers, a row the read could not answer).
    if (writer.door === 'person_identity' && writer.target?.contact_id) openPeek(openContactPeek(writer.target.contact_id));
  };

  if (error) return <p className="text-xs text-danger">{error}</p>;
  if (!rec) return <p className="text-xs italic text-text-dim">Loading the record…</p>;
  const a = rec.account;
  const incomplete = rec.counts?.incomplete || 0;
  const dealId = a.current_deal_id;

  return (
    <div className="space-y-4" data-client-record>
      {/* HEADER FACTS + THE INCOMPLETE CHIP */}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setFilter((v) => !v)} aria-pressed={filter} data-incomplete-chip
          className={`inline-flex min-h-[32px] items-center gap-2 rounded-pill border px-3 text-xs font-medium ${incomplete ? (filter ? 'border-danger bg-danger-soft text-danger' : 'border-border text-text hover:border-danger') : 'border-border text-text-dim'}`}>
          {incomplete ? <span className="inline-block h-2 w-2 rounded-full bg-danger" /> : null}
          Incomplete · {incomplete}
        </button>
        <button type="button" onClick={() => setShowMeta((v) => !v)} aria-pressed={showMeta} className="text-xs text-text-dim hover:text-text">{showMeta ? 'Hide sources' : 'Show sources'}</button>
        {a.crm_url ? <a href={a.crm_url} target="_blank" rel="noopener noreferrer" className="ml-auto text-xs text-text-dim hover:text-text">CRM ↗</a> : null}
      </div>
      <Card title={<span className="text-xs font-semibold uppercase tracking-[0.14em] text-text-muted">Named insured</span>}>
        <FactRow field="name" label="Name" fact={a.facts.name} lit={isLit('account', a.id, 'name')} {...rowProps} />
        <FactRow field="policyholder_type" label="Policyholder type" fact={a.facts.policyholder_type} lit={isLit('account', a.id, 'policyholder_type')} {...rowProps} />
        {/* 1c: the named-insured door refuses an address while the account has no
            policyholder type, so the type is asked first (the row above) and the
            address row stays read-only until the record says the type is known. */}
        <FactRow field="address" label="Mailing address" fact={a.facts.address} lit={isLit('account', a.id, 'address')} gatedBy={a.facts.policyholder_type.state === 'known' ? null : 'set the policyholder type first'} {...rowProps} />
        {/* FEIN: entities only (the record carries no row for an individual); FEIN-SPEC.md. */}
        {a.facts.fein ? <FactRow field="fein" label="FEIN" fact={a.facts.fein} lit={isLit('account', a.id, 'fein')} {...rowProps} /> : null}
      </Card>

      {/* TERMS */}
      <TermsChain terms={rec.terms} work={rec.work} />

      {/* AIRCRAFT */}
      <h3 className="pt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-dim">Aircraft {dealId ? <button type="button" className="ml-2 normal-case tracking-normal text-text-muted hover:text-text" onClick={() => setRosterOpen(true)}>Edit roster</button> : null}</h3>
      {rec.aircraft.length === 0 ? <p className="text-xs italic text-text-dim">No aircraft on file.</p> : null}
      {rec.aircraft.map((ac) => (
        <Card key={ac.tail} data-aircraft={ac.tail}
          title={<><span className="font-mono text-[15px] font-semibold text-text">{ac.tail}</span><span className="text-sm text-text-muted">{[ac.facts.year?.value, ac.facts.make?.value, ac.facts.model?.value].filter(Boolean).join(' ')}</span>{ac.status === 'historical' ? <span className="text-[10px] uppercase tracking-wide text-text-dim">former</span> : null}</>}
          right={ac.identity_unconfirmed ? <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full border border-text-dim" />from the FAA registry, unconfirmed</span> : ac.identity_tier === 'verified' ? <span>identity verified</span> : null}>
          {AIRCRAFT_ROWS.map(([f, l, k]) => <FactRow key={f} field={f} label={l} kind={k} options={optionsFor(f)} fact={ac.facts[f]} lit={isLit('aircraft', ac.tail, f)} onOpenDoor={openDoor} {...rowProps} />)}
          <button type="button" className="w-full py-2 text-left text-xs text-text-muted hover:text-text" onClick={() => setRegistryOpen((o) => ({ ...o, [ac.tail]: !o[ac.tail] }))} aria-expanded={!!registryOpen[ac.tail]}>
            {registryOpen[ac.tail] ? 'Fewer' : `${REGISTRY_ROWS.length} more from the registry · serial, class, engine, gear, weight`} {registryOpen[ac.tail] ? '▾' : '▸'}
          </button>
          {registryOpen[ac.tail] ? REGISTRY_ROWS.map(([f, l, k]) => <FactRow key={f} field={f} label={l} kind={k} fact={ac.facts[f]} lit={isLit('aircraft', ac.tail, f)} onOpenDoor={openDoor} {...rowProps} />) : null}
          {/* paperwork-1 L1b-B3 (D8): this aircraft's documents, read when asked. */}
          <div className="py-1.5"><AircraftDocuments tail={ac.tail} /></div>
        </Card>
      ))}

      {/* PEOPLE */}
      <h3 className="pt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-dim">People {dealId ? <button type="button" className="ml-2 normal-case tracking-normal text-text-muted hover:text-text" onClick={() => setRosterOpen(true)}>Manage pilots</button> : null}</h3>
      {rec.people.length === 0 ? <p className="text-xs italic text-text-dim">No people on file.</p> : null}
      {rec.people.map((p) => (
        <Card key={p.roster_key || p.contact_id} data-person={p.contact_id || p.roster_key}
          title={<><span className="text-[15px] font-semibold text-text">{p.name || 'Unnamed pilot'}</span><span className="text-sm text-text-muted">{p.role}{p.status === 'historical' ? ' · former' : ''}</span></>}
          right={p.pilot === false ? (p.email ? <span>{p.email}</span> : null) : <>
            {p.phf_status ? <span>PHF {p.phf_status}{p.phf_submitted_at ? ` ${String(p.phf_submitted_at).slice(0, 10)}` : ''}{p.phf_source === 'crm' ? ' · crm' : ''}</span> : <span>no PHF</span>}
            {p.phf_id ? <button type="button" className="rounded border border-border px-2 py-0.5 text-xs text-text hover:border-border-strong" onClick={() => setPhfOpen({ id: p.phf_id, name: p.name })} data-verb="full-phf">Full PHF</button> : null}
            {p.make_person?.target ? <button type="button" data-make-person onClick={() => setMakePerson(p)} className="rounded border border-border px-2 py-0.5 text-xs text-accent hover:border-border-strong">Make a person</button> : null}
          </>}>
          {IDENTITY_ROWS.map(([f, l]) => <FactRow key={f} field={f} label={l} fact={p.facts[f]} lit={isLit('pilot', p.contact_id || p.roster_key, f)} onOpenDoor={openDoor} {...rowProps} />)}
          {p.pilot === false ? null : PILOT_ROWS.map(([f, l, k]) => <FactRow key={f} field={f} label={l} kind={k} options={optionsFor(f)} fact={p.facts[f]} lit={isLit('pilot', p.contact_id || p.roster_key, f)} onOpenDoor={openDoor} {...rowProps} />)}
        </Card>
      ))}

      <div className="flex flex-wrap gap-4 text-xs text-text-dim">
        <span>Documents, quotes, communications, trust, credits and certificate holders continue below, unchanged.</span>
        {dealId ? <Link to={`/account/${encodeURIComponent(accountId)}/deal/${encodeURIComponent(dealId)}`} className="ml-auto hover:text-text hover:underline">Open the current term →</Link> : null}
      </div>

      <PhfSubmissionSheet phfId={phfOpen?.id || null} pilotName={phfOpen?.name || ''} open={!!phfOpen} onOpenChange={(o) => { if (!o) { setPhfOpen(null); saved(); } }} />
      <MakePersonSheet target={makePerson?.make_person?.target || null} prefill={makePerson?.prefill || null} pilotName={makePerson?.name || ''} open={!!makePerson} onOpenChange={(o) => { if (!o) setMakePerson(null); }} onMade={saved} />
      {rosterOpen && dealId ? (
        <Suspense fallback={null}>
          <RosterSheet dealId={dealId} open={rosterOpen} onOpenChange={setRosterOpen} accountName={a.name} onChange={saved} />
        </Suspense>
      ) : null}
    </div>
  );
}
